(ns ui.components.virtuoso
  (:require
   ["react-virtuoso" :refer [Virtuoso VirtuosoGrid]]))

;; NOTE:
;; - In Reagent, kebab-case keys are camelCased when passed to React components.
;;   So :horizontal-direction => horizontalDirection (react-virtuoso uses this boolean prop to enable horizontal mode).
;; - If callers accidentally pass :horizontalDirection (camelCase keyword),
;;   we normalize it to :horizontal-direction to keep everything consistent.
;; - DO NOT strip :horizontal or :horizontal-direction here — these are valid
;;   Virtuoso props and must reach the Virtuoso component (not a DOM node).

;; Normalize Virtuoso props:
;; - convert :horizontalDirection (camelCase) to :horizontal-direction (kebab-case)
;; - allow :horizontal (boolean) as an alias for :horizontal-direction
(defn- normalize-props
  [props]
  (let [props (or props {})
        ;; normalize camelCase keyword -> kebab-case keyword
        props (if (contains? props :horizontalDirection)
                (-> props
                    (assoc :horizontal-direction (:horizontalDirection props))
                    (dissoc :horizontalDirection))
                props)
        ;; allow :horizontal as a convenience alias for Virtuoso's horizontalDirection
        props (if (contains? props :horizontal)
                (-> props
                    (assoc :horizontal-direction (boolean (:horizontal props)))
                    (dissoc :horizontal))
                props)]
    props))

;; Thin wrapper over react-virtuoso Virtuoso.
;;
;; IMPORTANT: pass Virtuoso props here (NOT to a surrounding :div).
;;
;; Example:
;;   [virtuoso {:style {:height 400}
;;              :total-count 100
;;              :item-content (fn [idx] [:div (str idx)])
;;              :horizontal-direction true}]
;;
;; If you pass :horizontalDirection (camelCase keyword), it will be normalized.
;; If you pass :horizontal (alias), it will be normalized to :horizontal-direction.
;;
;; NOTE: This component takes ONLY a props map. If you accidentally pass extra
;; args/children, we log a warning so you can find the incorrect call site.
(defn virtuoso*
  "Thin wrapper over react-virtuoso Virtuoso.

  IMPORTANT: pass Virtuoso props here (NOT to a surrounding :div).

  Example:
    [virtuoso {:style {:height 400}
               :total-count 100
               :item-content (fn [idx] [:div (str idx)])
               :horizontal-direction true}]

  If you pass :horizontalDirection (camelCase keyword), it will be normalized.
  If you pass :horizontal (alias), it will be normalized to :horizontal-direction.

  NOTE: This component takes ONLY a props map. If you accidentally pass extra
  args/children, we log a warning so you can find the incorrect call site.
  "
  [props & more]
  (when (seq more)
    (js/console.warn "Virtuoso: extra args were passed (children). This wrapper expects ONLY a props map. Extra args:" (clj->js more)))
  (let [normalized-props (normalize-props props)]
    (when-not (contains? normalized-props :horizontal-direction)
      (js/console.warn "Virtuoso: missing :horizontal-direction / :horizontalDirection in props. Keys:" (clj->js (keys normalized-props))))
    #_(js/console.log "Virtuoso : " props "\n" normalized-props)
    ;; Use :> to ensure props go to the React component, not a DOM element.
    [:> Virtuoso normalized-props]))

(def virtuoso virtuoso*)

(defn virtuoso-grid
  "Thin wrapper over react-virtuoso VirtuosoGrid.

  Same prop normalization rules as `virtuoso`."
  [props]
  (let [props (normalize-props props)]
    [:> VirtuosoGrid props]))
